'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Gauge, HelpCircle, Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { EXPORT_FRAMING } from './framing-data';
import { LENS_META, LENS_ORDER, REASONS_BY_LENS } from './reasons-data';
import { buildSnapshot, EMPTY_ANSWERS } from './reality-check';
import { ReasonCard, type ReasonCardAccent, type ReasonCardDisplayProps } from './ReasonCard';
import { RealityCheckSummary } from './RealityCheckSummary';
import type { ExportLens, ExportRealityCheckAnswers, ExportReasonResponse, ExportRealityCheckProps } from './types';

/** Steps: intro framing, then each lens, then results */
export type ExportRealityCheckStep = 'intro' | ExportLens | 'results';
const STEP_ORDER: ExportRealityCheckStep[] = ['intro', ...LENS_ORDER, 'results'];
const STEP_LABELS: Record<ExportRealityCheckStep, string> = {
  intro: 'Introduction',
  why: 'Why do you want to export?',
  should: 'Should you export?',
  ready: 'Are you ready?',
  results: 'Assessment'
};
interface LensTheme {
  icon: typeof Scale;
  badge: string;
  rule: string;
  accent: ReasonCardAccent;
}
const LENS_THEME: Record<ExportLens, LensTheme> = {
  why: {
    icon: HelpCircle,
    badge: 'bg-amber-50 text-amber-700',
    rule: 'bg-amber-200',
    accent: {
      barIdle: 'border-l-amber-200',
      barActive: 'border-l-amber-500',
      challengeBg: 'bg-amber-50/70',
      challengeText: 'text-amber-900',
      iconColor: 'text-amber-500'
    }
  },
  should: {
    icon: Scale,
    badge: 'bg-teal-50 text-teal-700',
    rule: 'bg-teal-200',
    accent: {
      barIdle: 'border-l-teal-200',
      barActive: 'border-l-teal-500',
      challengeBg: 'bg-teal-50/70',
      challengeText: 'text-teal-900',
      iconColor: 'text-teal-500'
    }
  },
  ready: {
    icon: Gauge,
    badge: 'bg-emerald-50 text-emerald-700',
    rule: 'bg-emerald-200',
    accent: {
      barIdle: 'border-l-emerald-200',
      barActive: 'border-l-emerald-500',
      challengeBg: 'bg-emerald-50/70',
      challengeText: 'text-emerald-900',
      iconColor: 'text-emerald-500'
    }
  }
};
export interface ExportRealityCheckStepProps extends ExportRealityCheckProps {
  /** Controlled active step. Defaults to 'intro'. */
  activeStep?: ExportRealityCheckStep;
  /** Callback when the step changes. */
  onStepChange?: (step: ExportRealityCheckStep) => void;
  /** Props forwarded to every ReasonCard to control display behavior. */
  reasonCardProps?: ReasonCardDisplayProps;
}
export function ExportRealityCheck(t0) {
  const $ = _c(74);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialAnswers,
    reasonResources: t2,
    reasonExtensions: t3,
    summaryExtension,
    activeStep: controlledStep,
    onStepChange,
    reasonCardProps,
    onChange,
    onComplete
  } = t1;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const reasonResources = t4;
  let t5;
  if ($[4] !== t3) {
    t5 = t3 === undefined ? {} : t3;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const reasonExtensions = t5;
  const [internalStep, setInternalStep] = useState("intro");
  const step = controlledStep ?? internalStep;
  let t6;
  if ($[6] !== controlledStep || $[7] !== onStepChange) {
    t6 = next => {
      if (!controlledStep) {
        setInternalStep(next);
      }
      onStepChange?.(next);
      setTimeout(() => {
        const element = rootRef.current;
        if (element) {
          element.scrollIntoView({
            behavior: "instant",
            block: "start"
          });
        }
        if (typeof window !== "undefined" && window.self !== window.top) {
          window.parent.postMessage({
            type: "embed-scroll-top"
          }, "*");
        }
      }, 150);
    };
    $[6] = controlledStep;
    $[7] = onStepChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setStep = t6;
  let t7;
  if ($[9] !== initialAnswers?.reflection || $[10] !== initialAnswers?.responses) {
    t7 = () => ({
      reflection: {
        ...EMPTY_ANSWERS.reflection,
        ...initialAnswers?.reflection
      },
      responses: {
        ...EMPTY_ANSWERS.responses,
        ...initialAnswers?.responses
      }
    });
    $[9] = initialAnswers?.reflection;
    $[10] = initialAnswers?.responses;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const [answers, setAnswers] = useState(t7);
  const [startedAt] = useState(_temp);
  const [completedAt, setCompletedAt] = useState();
  const rootRef = useRef(null);
  let t8;
  if ($[12] !== answers || $[13] !== completedAt || $[14] !== startedAt) {
    t8 = buildSnapshot({
      answers,
      startedAt,
      completedAt
    });
    $[12] = answers;
    $[13] = completedAt;
    $[14] = startedAt;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const snapshot = t8;
  let t10;
  let t9;
  if ($[16] !== onChange || $[17] !== snapshot) {
    t9 = () => {
      onChange?.(snapshot);
    };
    t10 = [onChange, snapshot];
    $[16] = onChange;
    $[17] = snapshot;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = function handleResponseChange(reasonId, next_0) {
      setAnswers(current => {
        const responses = {
          ...current.responses
        };
        if (!next_0.applicability && !next_0.note) {
          delete responses[reasonId];
        } else {
          responses[reasonId] = next_0;
        }
        return {
          ...current,
          responses
        };
      });
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleResponseChange = t11;
  let t12;
  if ($[21] !== answers || $[22] !== onComplete || $[23] !== setStep || $[24] !== startedAt || $[25] !== step) {
    t12 = function goNext() {
      const currentIndex = STEP_ORDER.indexOf(step);
      const nextStep = STEP_ORDER[currentIndex + 1];
      if (nextStep) {
        if (nextStep === "results") {
          const finishedAt = new Date().toISOString();
          setCompletedAt(finishedAt);
          onComplete?.(buildSnapshot({
            answers,
            startedAt,
            completedAt: finishedAt
          }));
        }
        setStep(nextStep);
      }
    };
    $[21] = answers;
    $[22] = onComplete;
    $[23] = setStep;
    $[24] = startedAt;
    $[25] = step;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const goNext = t12;
  let t13;
  if ($[27] !== setStep || $[28] !== step) {
    t13 = function goPrev() {
      const currentIndex_0 = STEP_ORDER.indexOf(step);
      const prevStep = STEP_ORDER[currentIndex_0 - 1];
      if (prevStep) {
        setStep(prevStep);
      }
    };
    $[27] = setStep;
    $[28] = step;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const goPrev = t13;
  let t14;
  if ($[30] !== setStep) {
    t14 = function handleReset() {
      setAnswers({
        reflection: {},
        responses: {}
      });
      setCompletedAt(undefined);
      setStep("intro");
    };
    $[30] = setStep;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const handleReset = t14;
  const {
    summary
  } = snapshot;
  const currentStepIndex = STEP_ORDER.indexOf(step);
  let t15;
  if ($[32] !== step) {
    t15 = LENS_ORDER.includes(step as ExportLens);
    $[32] = step;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const isLensStep = t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Sections</p>;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== currentStepIndex || $[36] !== setStep || $[37] !== step) {
    t17 = STEP_ORDER.map(s => {
      const isActive = s === step;
      const isPast = STEP_ORDER.indexOf(s) < currentStepIndex;
      return <button key={s} type="button" onClick={() => {
        setStep(s);
      }} className={cn("block w-full text-left rounded-md px-3 py-2 text-sm transition-colors cursor-pointer", isActive ? "bg-gray-900 text-white font-medium" : isPast ? "text-gray-700 hover:bg-gray-100" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600")}>{STEP_LABELS[s]}</button>;
    });
    $[35] = currentStepIndex;
    $[36] = setStep;
    $[37] = step;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17) {
    t18 = <aside className="hidden lg:block w-56 shrink-0"><nav className="sticky top-24 space-y-1">{t16}{t17}</nav></aside>;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== setStep) {
    t19 = e => {
      setStep(e.target.value as ExportRealityCheckStep);
    };
    $[41] = setStep;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = STEP_ORDER.map(_temp2);
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== step || $[45] !== t19) {
    t21 = <div className="mb-6 lg:hidden"><select value={step} onChange={t19} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-900 cursor-pointer">{t20}</select></div>;
    $[44] = step;
    $[45] = t19;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== goNext || $[48] !== step) {
    t22 = step === "intro" && <><section><h1 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">Get clear on whether exporting is really right for your business and what you need to know first</h1><p className="mt-6 text-[15px] leading-relaxed text-gray-700">Many New Zealand businesses decide to export in the hope of solving commercial problems they're having in their domestic market. Some of these motivating factors include:</p><ul className="mt-4 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-gray-700 marker:text-gray-400"><li>Feeling like there's too much competition here (like you're being pushed out)</li><li>Getting pressure from your board or external advisors to significantly increase year-on-year revenue</li><li>Struggling to do well here in New Zealand</li></ul><p className="mt-6 text-[15px] leading-relaxed text-gray-700">What these companies often find out — after spending significant time and money — is that exporting is not always the answer.</p><div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-5"><p className="text-sm font-semibold text-gray-900">Ask yourself honestly</p><ol className="mt-3 list-decimal space-y-2 pl-5 text-[15px] leading-relaxed text-gray-700 marker:font-medium marker:text-gray-500">{EXPORT_FRAMING.betterQuestions.map(_temp3)}</ol></div><div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-5"><p className="text-sm font-semibold text-gray-900">Often the better move is</p><ul className="mt-3 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-gray-700 marker:text-gray-400">{EXPORT_FRAMING.alternatives.map(_temp4)}</ul></div><blockquote className="mt-8 border-l-2 border-teal-500 pl-4 text-[15px] font-medium italic leading-relaxed text-gray-600">{EXPORT_FRAMING.reframe}</blockquote><p className="mt-6 text-[15px] leading-relaxed text-gray-700">Before you decide to invest in entering an export market, it's important to sense-check your motives for wanting to export in the first place. Get clear on your long-term business goals, so you can see if exporting is the next best step.</p></section><StepNavigation onNext={goNext} nextLabel="Start: Why do you want to export?" /></>;
    $[47] = goNext;
    $[48] = step;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== answers || $[51] !== goNext || $[52] !== goPrev || $[53] !== isLensStep || $[54] !== reasonCardProps || $[55] !== reasonExtensions || $[56] !== reasonResources || $[57] !== step || $[58] !== summary) {
    t23 = isLensStep && (() => {
      const lens = step as ExportLens;
      const meta = LENS_META[lens];
      const theme = LENS_THEME[lens];
      return <><section><h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">{meta.label}</h2><p className="mt-3 text-base leading-relaxed text-gray-600">{meta.description}</p><div className={cn("mt-6 h-px", theme.rule)} /><div className="mt-8 space-y-6">{REASONS_BY_LENS[lens].map(reason => <ReasonCard key={reason.id} reason={reason} accent={theme.accent} response={answers.responses[reason.id]} extraResources={reasonResources[reason.id]} extension={reasonExtensions[reason.id]} onChange={handleResponseChange} {...reasonCardProps} />)}</div>{lens === "why" && <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50/40 p-5"><label htmlFor="why-freeform" className="mb-2 block text-sm font-medium text-gray-700">Anything else driving your interest in exporting?</label><Textarea id="why-freeform" rows={2} value={answers.reflection.motivation ?? ""} onChange={event => setAnswers(current_0 => ({
              ...current_0,
              reflection: {
                ...current_0.reflection,
                motivation: event.target.value || undefined
              }
            }))} placeholder="e.g. A specific market opportunity, a partnership offer, personal ambition..." className="text-sm bg-white" /></div>}</section><StepNavigation onPrev={goPrev} onNext={goNext} nextLabel={step === "ready" ? summary.answeredCount === 0 ? "Answer at least one to see results" : "See where you land" : undefined} nextDisabled={step === "ready" && summary.answeredCount === 0} /></>;
    })();
    $[50] = answers;
    $[51] = goNext;
    $[52] = goPrev;
    $[53] = isLensStep;
    $[54] = reasonCardProps;
    $[55] = reasonExtensions;
    $[56] = reasonResources;
    $[57] = step;
    $[58] = summary;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== goPrev || $[61] !== handleReset || $[62] !== snapshot || $[63] !== step || $[64] !== summaryExtension) {
    t24 = step === "results" && <RealityCheckSummary snapshot={snapshot} extension={summaryExtension} onBack={goPrev} onReset={handleReset} />;
    $[60] = goPrev;
    $[61] = handleReset;
    $[62] = snapshot;
    $[63] = step;
    $[64] = summaryExtension;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== t21 || $[67] !== t22 || $[68] !== t23 || $[69] !== t24) {
    t25 = <div className="min-w-0 flex-1 max-w-2xl">{t21}{t22}{t23}{t24}</div>;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t18 || $[72] !== t25) {
    t26 = <div ref={rootRef} className="flex gap-10 scroll-mt-20">{t18}{t25}</div>;
    $[71] = t18;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  return t26;
}

/** Navigation buttons between steps */
function _temp4(item) {
  return <li key={item}>{item}</li>;
}
function _temp3(question) {
  return <li key={question}>{question}</li>;
}
function _temp2(s_0) {
  return <option key={s_0} value={s_0}>{STEP_LABELS[s_0]}</option>;
}
function _temp() {
  return new Date().toISOString();
}
function StepNavigation(t0) {
  const $ = _c(9);
  const {
    onPrev,
    onNext,
    nextLabel: t1,
    nextDisabled: t2
  } = t0;
  const nextLabel = t1 === undefined ? "Next section" : t1;
  const nextDisabled = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== onPrev) {
    t3 = onPrev ? <button type="button" onClick={onPrev} className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 cursor-pointer"><ArrowLeft className="h-4 w-4" />Back</button> : <div />;
    $[0] = onPrev;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== nextDisabled || $[3] !== nextLabel || $[4] !== onNext) {
    t4 = onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className={cn("flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer", nextDisabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : "bg-gray-900 text-white hover:bg-gray-800")}>{nextLabel}<ArrowRight className="h-4 w-4" /></button>;
    $[2] = nextDisabled;
    $[3] = nextLabel;
    $[4] = onNext;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="mt-10 flex items-center justify-between border-t border-gray-200 pt-6">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}